import { useCartStore } from '@/stores/cart'
import { createRouter, createWebHashHistory } from 'vue-router'
import type { ErrorTypes, NavigationFailure } from 'vue-router'

// components must be imported and not lazyloaded
// else it messes up totally with the CSS and causes a lot of issues
import PackagesView from '@/pages/booking-funnel/PackagesView.vue'
import MeetingRoomsView from '@/pages/booking-funnel/MeetingRoomsView.vue'
import PackageConfigurationView from '@/pages/booking-funnel/PackageConfigurationView.vue'
import CheckoutView from '@/pages/booking-funnel/CheckoutView.vue'
import { useModalsStore } from '@/stores/modals'
import { createI18nConfig, fetchLocales } from '../vue/i18n'
import { useGtm } from '@/composables/features/useGtm'
import { usePackagesStore } from '@/stores/packages'
import { storeToRefs } from 'pinia'
import { useConfigurations } from '@/stores/configurations'

export const router = createRouter({
  history: createWebHashHistory(),
  routes: [
    {
      path: '/',
      name: 'package-search',
      component: PackagesView,
      beforeEnter: async (_to, from) => {
        /*
        if the user already had a cart and comes back to package selection,
        we clear the cart
        */
        useConfigurations().general.warnUnload = false
        const cartStore = useCartStore()
        if (from.name && cartStore.cart) {
          const modalStore = useModalsStore()
          // Pre-load locales
          await fetchLocales()
          // Warning: "Type instantiation is excessively deep and possibly infinite.ts(2589)"
          // @ts-expect-error
          const { t } = createI18nConfig().global

          // Translation with i18n of this modal text is quite particular here...
          const response = await modalStore.confirm({
            title: t('Are you sure?'),
            body: t('This action will empty your basket'),
            confirmButton: t('Yes, edit my selection'),
            cancelButton: t('No, keep me here'),
          })

          if (response) {
            const cartStore = useCartStore()
            cartStore.$reset()
          } else {
            return false
          }
        }
      },
    },
    {
      path: '/venues',
      name: 'meeting-room-search',
      component: MeetingRoomsView,
      meta: {
        requiresPackage: true,
      },
      beforeEnter: async (_to, from) => {
        /*
        if the user came back to the venue
        clear the url query "meetingRoomID"
        */
        useConfigurations().general.warnUnload = false
        if (from.name !== 'package-search') {
          const packagesStore = usePackagesStore()
          const { meetingRoomID } = storeToRefs(packagesStore)
          meetingRoomID.value = undefined
        }
      },
    },
    {
      path: '/configure',
      name: 'package-configuration',
      meta: {
        requiresPackage: true,
        requiresFunctionRoom: true,
      },
      component: PackageConfigurationView,
      beforeEnter: async (_to, from, next) => {
        /*
        if the user leave the venue through the stepper (so without select a venue)
        define the url query "meetingRoomID" from the card functionRoom value
        */
        useConfigurations().general.warnUnload = true
        if (from.name === 'meeting-room-search') {
          const packagesStore = usePackagesStore()
          const { meetingRoomID, availableVenues } = storeToRefs(packagesStore)
          if (!meetingRoomID.value) {
            const cartStore = useCartStore()
            meetingRoomID.value = cartStore.cart?.functionRoomId
          }
          if (typeof meetingRoomID.value === 'object') {
            meetingRoomID.value = availableVenues.value.find(
              (galaxyMeetingRoom) =>
                galaxyMeetingRoom.db2_eventroomid === meetingRoomID?.value?.db2_eventroomid,
            )
          } else if (typeof meetingRoomID.value === 'string') {
            meetingRoomID.value = availableVenues.value.find(
              (galaxyMeetingRoom) => galaxyMeetingRoom.db2_eventroomid === meetingRoomID.value,
            )
          }
          const cartStore = useCartStore()
          if (
            typeof meetingRoomID.value === 'undefined' &&
            cartStore.cart &&
            cartStore.cart.functionRoomId
          ) {
            cartStore.cart.functionRoomId = undefined
            const configStore = useConfigurations()
            if (configStore.funnel.showVenueSelection) {
              next('/venues')
            } else {
              next('/')
            }
          }
        }
        next()
      },
    },
    {
      path: '/checkout',
      name: 'checkout',
      meta: {
        requiresPackage: true,
        requiresFunctionRoom: true,
      },
      component: CheckoutView,
      beforeEnter: async () => {
        useConfigurations().general.warnUnload = true
      },
    },
    {
      path: '/:pathMatch(.*)*',
      redirect: '/',
      beforeEnter: async () => {
        useConfigurations().general.warnUnload = false
      },
    },
  ],
  scrollBehavior() {
    // always scroll to top
    return { top: 0 }
  },
})

router.beforeEach((to) => {
  const cartStore = useCartStore()
  if (to.meta.requiresPackage && !cartStore.cart?.packageId) {
    return { name: 'package-search' }
  }
  if (to.meta.requiresFunctionRoom && !cartStore.cart?.functionRoomId) {
    return { name: 'meeting-room-search' }
  }
})

function isNavigationFailure(
  failure: NavigationFailure | undefined,
  navigationFailureType:
    | ErrorTypes.NAVIGATION_ABORTED
    | ErrorTypes.NAVIGATION_CANCELLED
    | ErrorTypes.NAVIGATION_DUPLICATED,
): boolean {
  if (!(failure instanceof Error)) {
    return false
  }
  return !!(failure.type & navigationFailureType)
}

const { virtualPageFunnel } = useGtm()

router.afterEach(async (to, from, failure) => {
  // @ts-expect-error
  if (isNavigationFailure(failure, 4 /* NAVIGATION_ABORTED */)) {
    console.debug(`[Gtm]: '${String(to.name)}' not tracked due to navigation aborted`)
  } else {
    // @ts-expect-error
    if (isNavigationFailure(failure, 8)) {
      console.debug(`[Gtm]: '${String(to.name)}' not tracked due to navigation cancelled`)
    } else {
      virtualPageFunnel(String(to.name))
    }
  }
})
